import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { memo, useState, useCallback, useEffect } from 'react';

import Iconify from 'src/components/iconify';

import { NavProps, NavGroupProps } from '../types';
import NavList from './nav-list';

// Sincronização de estado recolhido/expandido de todas as gavetas

// ----------------------------------------------------------------------

function NavSectionVertical({ data, slotProps, ...other }: NavProps) {
  return (
    <Stack component="nav" id="nav-section-vertical" {...other}>
      {data.map((group, index) => (
        <Group key={group.subheader || index} subheader={group.subheader} items={group.items} slotProps={slotProps} />
      ))}
    </Stack>
  );
}

export default memo(NavSectionVertical);

// ----------------------------------------------------------------------

function Group({ subheader, items, slotProps }: NavGroupProps) {
  const [open, setOpen] = useState(true);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (slotProps?.allCollapsed !== undefined) {
      setOpen(!slotProps.allCollapsed);
    }
  }, [slotProps?.allCollapsed]);

  const renderContent = items.map((list) => <NavList key={list.title} data={list} depth={1} slotProps={slotProps} />);

  const showClearFavoritesAction = subheader === 'Favoritos' && !!slotProps?.onClearFavorites;

  return (
    <Stack sx={{ px: slotProps?.collapsed ? 2 : 2 }}>
      {subheader && !slotProps?.collapsed ? (
        <>
          <ListSubheader
            disableGutters
            disableSticky
            onClick={handleToggle}
            sx={{
              fontSize: 11,
              cursor: 'pointer',
              typography: 'overline',
              display: 'inline-flex',
              color: 'text.disabled',
              mb: `${slotProps?.gap || 4}px`,
              p: (theme) => theme.spacing(2, 1, 1, 1.5),
              transition: (theme) =>
                theme.transitions.create(['color'], {
                  duration: theme.transitions.duration.shortest,
                }),
              '&:hover': {
                color: 'text.primary',
              },
              ...slotProps?.subheader,
            }}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flex: '1 1 auto' }}>
              {subheader}
              <Iconify
                width={12}
                icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
                sx={{ ml: 0.5, color: 'text.disabled' }}
              />
              {showClearFavoritesAction && (
                <Tooltip
                  arrow
                  title="Desfavoritar tudo"
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: { fontSize: 13 },
                    },
                  }}
                >
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    aria-label="Desfavoritar tudo"
                    onClick={(event) => {
                      event.stopPropagation();
                      slotProps?.onClearFavorites?.();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        slotProps?.onClearFavorites?.();
                      }
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      ml: 0.75,
                      color: 'grey.500',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'grey.700',
                      },
                    }}
                  >
                    <Iconify icon="tabler:star-off" width={14} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </ListSubheader>

          <Collapse in={open}>{renderContent}</Collapse>
        </>
      ) : (
        renderContent
      )}
    </Stack>
  );
}
